import { CreditCardIcon } from '@phosphor-icons/react';
import { Card, CardHeading } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/text';
import { EmptyState } from '@/components/ui/empty-state';
import { PILL_BASE } from '@/components/cms/ui/pill-styles';
import { cn } from '@/utils/utils';
import { OrgInstance } from '@/types/orgTypes';

// The plan's pill in the ink when active, the secondary on a running voucher, else outlined
const PLAN_PILL = {
  active: 'py-0.5 bg-lureo-ink-strong border-transparent text-lureo-surface',
  voucher: 'py-0.5 bg-lureo-secondary border-transparent text-lureo-on-secondary',
  other: 'py-0.5 border-lureo-line text-lureo-ink-strong',
} as const;

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  unpaid: 'Unpaid',
  canceled: 'Cancelled',
  cancelled: 'Cancelled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
};

function subscriptionStatus(instance: OrgInstance, now = Date.now()): { label: string; tone: keyof typeof PLAN_PILL } {
  const status = instance.orgInstanceSubscriptionStatus ?? '';
  if (status === 'voucher') {
    const ends = instance.orgInstanceNextBillingDate ? Date.parse(instance.orgInstanceNextBillingDate) : NaN;
    return ends < now ? { label: 'Trial ended', tone: 'other' } : { label: 'Trial', tone: 'voucher' };
  }
  const label = STATUS_LABELS[status] ?? (status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') : 'Unknown');
  return { label, tone: status === 'active' ? 'active' : 'other' };
}

const capitalised = (text?: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

/** My Account's Subscription tab, for the org's owner: the plan, its real status, and the org's details. */
export function SubscriptionCard({ orgInstance }: { orgInstance: OrgInstance | null }) {
  const status = orgInstance ? subscriptionStatus(orgInstance) : null;
  return (
    <Card elevation="high">
      <CardHeading title="Organization Subscription" description="Manage your organization's subscription and billing" className="mb-6" />
      {orgInstance && status ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border-lureo-border-width rounded-lureo-radius-lg bg-lureo-surface/50">
              <CardTitle as="h3" className="mb-3 text-lureo-14 leading-5 text-lureo-ink-subtle">Current Plan</CardTitle>
              <div className="space-y-2">
                <p className="text-lureo-24 font-bold text-lureo-ink-strong leading-8">
                  {capitalised(orgInstance.orgInstanceSubscriptionPlan)}
                </p>
                <span className={cn(PILL_BASE, PLAN_PILL[status.tone])}>{status.label}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lureo-radius-lg border-lureo-border-width p-4 bg-lureo-surface/50">
            <CardTitle as="h3" className="mb-3 text-lureo-16 leading-6">Organization Details</CardTitle>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-lureo-14 text-lureo-ink-subtle leading-5">Organization Name:</span>
                <span className="text-lureo-14 font-medium text-lureo-ink-strong leading-5">{orgInstance.orgInstanceDisplayName || 'Unnamed Organization'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-lureo-14 text-lureo-ink-subtle leading-5">Organization ID:</span>
                <span className="text-lureo-14 font-lureo-font-mono text-lureo-ink  leading-5">{orgInstance.orgInstanceId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-lureo-14 text-lureo-ink-subtle leading-5">Created:</span>
                <span className="text-lureo-14 font-medium text-lureo-ink-strong leading-5">
                  {new Date(orgInstance.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <EmptyState icon={<CreditCardIcon />} title="No organization data" size="roomy">
          Unable to load organization subscription information.
        </EmptyState>
      )}
    </Card>
  );
}
